/**
 * The post-season promotions contract — bata-ui issue 314, read built by bata-platform issue 580
 * from bata-data's marts (bata-data ADR 8006, the windows inferred from the tills; ADR 8007, their
 * impact against the neighbouring days). `docs/adr/8004` (the three pages) and `docs/adr/9051`
 * (the dot-and-interval chart form).
 *
 * ══ PINNED 2026-10-07 ═════════════════════════════════════════════════════════
 *
 * The client was re-pinned to bata-platform main at `17950e3`, which publishes all three reads
 * (platform PR 583, ADR 8012). Each `…_READ` below is now the generated operation, so the pages
 * issue their reads, and each `…Shape` is the generated model re-exported under the name the
 * readers already used — the generated models matched the hand-typed shapes field for field, in
 * both directions, so no reader changed. The params builders keep their stricter types (both
 * dates required, G16); the generated params mark every query member optional and accept them.
 * What follows is the text as it stood before the pin.
 *
 * bata-platform merged the reads (PR 583, ADR 8012), but `src/api/spec/openapi.json` is still pinned
 * to a contract that does not publish them, so — exactly as
 * `service-tradeoff.ts` did before its re-pin (ADR 9037's "built before its read lands") — the
 * shapes below are typed by hand from the shared contract, each `…_READ` constant is `null`, and
 * every page draws its designed «In arrivo» state and issues NO request. The `…_PROPOSED_OPERATION`
 * constants are the operations as the contract declares them; a test mounts each page over one
 * with a stand-in `fetch`, so the whole rendering is exercised before the re-pin.
 *
 * `src/features/postseason/promotions/promotions.test.tsx` keeps the tripwire: the pinned spec has
 * each path exactly when its constant is wired. When bata-platform 580 lands, the re-pin (1)
 * re-exports the generated shapes under the names below, (2) sets each `…_READ` to the generated
 * operation, (3) keeps the readers (`promotionWindowsOf`, `promotionMechanicsOf`) — the pages read
 * ONLY what they build. **Every field access of the platform's shapes is in this file.**
 *
 *   GET /api/v1/postseason/promotions/windows?country_key=&from=&to=     the windows in a range
 *   GET /api/v1/postseason/promotions/mechanics?country_key=              per mechanic × channel
 *   GET /api/v1/postseason/promotions/calendar?country_key=&year=         the commercial weeks
 *
 * The shapes are bata-platform's response models as merged (`services/api/routers/
 * postseason_promotions.py`, 942f472): the windows read answers `{ countryKey, from, to, windows }`,
 * each window nesting `impacts` and `overlappingWeeks` (`label` the full calendar week, or `null`
 * when none landed); a window is in range when it shares a day with `[from, to]`, inclusive. The
 * mechanics read answers `{ countryKey, mechanics }`, each row with its own `basis` and the net
 * uplift always present as `{ estimate, low, high }` — `low`/`high` `null` below two windows. Money
 * is exact decimal TEXT in the row's `currency`: shown as written, never parsed and added. Neither
 * read is paged. The calendar read is not drawn by a page (the windows nest their weeks); it is
 * typed here so the re-pin wires all three at once. With nothing landed each answers 404
 * {@link PROMOTIONS_UNAVAILABLE}; an inverted range answers 422 {@link PROMOTIONS_RANGE_INVALID}.
 */

import {
  promotionCalendar, promotionMechanics, promotionWindowArticles, promotionWindowDoors, promotionWindowDoorSubcategories, promotionWindows,
  type OperationSpec,
} from '../generated/operations';
import type {
  PromotionCalendarParams as GeneratedPromotionCalendarParams,
  PromotionMechanicsParams as GeneratedPromotionMechanicsParams,
  PromotionWindowArticlesParams as GeneratedPromotionWindowArticlesParams,
  PromotionWindowDoorsParams as GeneratedPromotionWindowDoorsParams,
  PromotionWindowDoorSubcategoriesParams as GeneratedPromotionWindowDoorSubcategoriesParams,
  PromotionWindowsParams as GeneratedPromotionWindowsParams,
} from '../generated/operations';
import type {
  CommercialWeekModel, DoorSubcategoriesModel, MechanicImpactModel, OverlappingWeekModel, PromotionCalendarModel, PromotionMechanicsModel,
  PromotionWindowModel, PromotionWindowsModel, UpliftIntervalModel, WindowArticlesModel, WindowDoorsModel, WindowImpactModel,
} from '../generated/types';

// ── the vocabularies ─────────────────────────────────────────────────────────

/**
 * The proposed registry vocabulary `promotion.mechanic_vocabulary` (bata-platform ADR 8011), in
 * its declared order — the order the mechanics are listed in, never a ranking. A code outside it
 * is read as no code: the page shows the mart's descriptive class instead.
 */
export const PROMOTION_MECHANICS = ['multi_buy', 'second_item_percent_off', 'article_percent_off'] as const;
export type PromotionMechanic = (typeof PROMOTION_MECHANICS)[number];

/**
 * The tills' window classes the pages have words for (bata-data ADR 8006 §6, ADR 8011).
 * `LINE_DEPTH` is a BASKET line (the second-item offer, `second_item_percent_off`);
 * `ARTICLE_LINE_DEPTH` is a line at a depth its articles hold or no basket carries — the sale
 * zone's markdown steps (`article_percent_off`). A class outside this list is said verbatim inside
 * a sentence that says this build has no words for it — never guessed at.
 */
export const WINDOW_CLASSES = ['FREE_ITEM', 'LINE_DEPTH', 'ARTICLE_LINE_DEPTH', 'LOW'] as const;
export type WindowClass = (typeof WINDOW_CLASSES)[number];

/** The headline parse's mechanics (bata-data ADR 8006 §2) — Bata's own label, read, not governed. */
export const LABEL_MECHANICS = ['MULTIBUY_3X2', 'MULTIBUY_2X1', 'SECOND_PAIR_PCT', 'SECOND_ARTICLE_PCT', 'SALE_ZONE_EOSS'] as const;
export type LabelMechanic = (typeof LABEL_MECHANICS)[number];

/** The one identification the impact mart publishes (bata-data ADR 8007 §2, §6). */
export const NEIGHBOUR_BASIS = 'neighbour_days_same_doors';

/** 404: no promotion source landed in the serving process — «In arrivo», never zeroes. */
export const PROMOTIONS_UNAVAILABLE = 'planning.postseason.promotions_unavailable';

/** 422: `from` is after `to` — a request defect, never a period with nothing in it. */
export const PROMOTIONS_RANGE_INVALID = 'planning.postseason.promotions_range_invalid';

/** 404: the window register in force holds no such window (bata-platform ADR 8016). */
export const PROMOTION_WINDOW_NOT_FOUND = 'planning.postseason.promotion_window_not_found';

/** 404: the door register in force has no row for the store around the window (bata-platform ADR 8017). */
export const PROMOTION_WINDOW_DOOR_NOT_FOUND = 'planning.postseason.promotion_window_door_not_found';

// ── read 1: the windows in a range ───────────────────────────────────────────

/** One commercial week with Bata's headline and its descriptive parse (`CommercialWeekModel`). */
export type CommercialWeekShape = CommercialWeekModel;

/** A commercial week a window shares days with; `label` `null` when none has landed. */
export type OverlappingWeekShape = OverlappingWeekModel;

/** One channel's impact of a window (`promotion-window-impact`). Rates are fractions. */
export type PromotionImpactShape = WindowImpactModel;

/** One window inferred from the tills (`promotion-window`), with its weeks and its impacts. */
export type PromotionWindowShape = PromotionWindowModel;

export type PromotionWindowsShape = PromotionWindowsModel;

export interface PromotionWindowsParams {
  readonly query: { readonly country_key: string; readonly from: string; readonly to: string };
}

export type PromotionWindowsRead = OperationSpec<GeneratedPromotionWindowsParams, PromotionWindowsShape>;

export const PROMOTION_WINDOWS_PATH_IN_SPEC = '/api/v1/postseason/promotions/windows';

/** The operation as the contract declares it. Not wired: tests mount the pages over it. */
export const PROMOTION_WINDOWS_PROPOSED_OPERATION: PromotionWindowsRead = {
  operationId: 'promotion_windows_api_v1_postseason_promotions_windows_get',
  method: 'GET',
  path: PROMOTION_WINDOWS_PATH_IN_SPEC,
  tag: 'postseason',
  pathParams: [],
  queryParams: ['country_key', 'from', 'to'],
  hasBody: false,
};

/** The generated operation (pinned 2026-10-07); `null` would draw «In arrivo» and read nothing. */
export const PROMOTION_WINDOWS_READ: PromotionWindowsRead | null = promotionWindows;

/** Both dates are required (G16): the read is never asked without them. */
export function promotionWindowsParams(market: string, from: string, to: string): PromotionWindowsParams {
  return { query: { country_key: market, from, to } };
}

// ── read 2: per mechanic × channel ───────────────────────────────────────────

/** An estimate with its 80% interval; `low`/`high` `null` together below two windows. */
export type UpliftIntervalShape = UpliftIntervalModel;

/** One inferred mechanic (`windowKey`) × store channel. Rate changes are fractions. */
export type PromotionMechanicShape = MechanicImpactModel;

export type PromotionMechanicsShape = PromotionMechanicsModel;

export interface PromotionMechanicsParams {
  readonly query: { readonly country_key: string };
}

export type PromotionMechanicsRead = OperationSpec<GeneratedPromotionMechanicsParams, PromotionMechanicsShape>;

export const PROMOTION_MECHANICS_PATH_IN_SPEC = '/api/v1/postseason/promotions/mechanics';

export const PROMOTION_MECHANICS_PROPOSED_OPERATION: PromotionMechanicsRead = {
  operationId: 'promotion_mechanics_api_v1_postseason_promotions_mechanics_get',
  method: 'GET',
  path: PROMOTION_MECHANICS_PATH_IN_SPEC,
  tag: 'postseason',
  pathParams: [],
  queryParams: ['country_key'],
  hasBody: false,
};

export const PROMOTION_MECHANICS_READ: PromotionMechanicsRead | null = promotionMechanics;

export function promotionMechanicsParams(market: string): PromotionMechanicsParams {
  return { query: { country_key: market } };
}

// ── read 3: the commercial calendar of a year ─────────────────────────────────

/** A year with nothing landed is an empty `weeks` list; nothing landed at all is the 404. */
export type PromotionCalendarShape = PromotionCalendarModel;

export interface PromotionCalendarParams {
  readonly query: { readonly country_key: string; readonly year: number };
}

export type PromotionCalendarRead = OperationSpec<GeneratedPromotionCalendarParams, PromotionCalendarShape>;

export const PROMOTION_CALENDAR_PATH_IN_SPEC = '/api/v1/postseason/promotions/calendar';

export const PROMOTION_CALENDAR_PROPOSED_OPERATION: PromotionCalendarRead = {
  operationId: 'promotion_calendar_api_v1_postseason_promotions_calendar_get',
  method: 'GET',
  path: PROMOTION_CALENDAR_PATH_IN_SPEC,
  tag: 'postseason',
  pathParams: [],
  queryParams: ['country_key', 'year'],
  hasBody: false,
};

/** Not drawn by a page today; wired with the other two at the 2026-10-07 re-pin. */
export const PROMOTION_CALENDAR_READ: PromotionCalendarRead | null = promotionCalendar;

/** The confidence the read states its intervals at (exp(log ± 1.2816·se) − 1). */
export const INTERVAL_CONFIDENCE = 0.8;

// ── the pages' reading ───────────────────────────────────────────────────────

/**
 * A mechanic as the pages say it. `code` is a vocabulary code this build knows, else `null`;
 * `registered` is the platform's statement that the code is in the vocabulary row in force (none
 * is approved today, so every window says it is not yet). The descriptive class is ALWAYS kept:
 * an unregistered mechanic is named by it, never hidden.
 */
export interface MechanicReading {
  readonly code: PromotionMechanic | null;
  /** The platform's code verbatim, known or not — `null` when it published none. */
  readonly rawCode: string | null;
  readonly registered: boolean;
}

/** The tills' class as the pages say it: a class this build has words for, else its code verbatim. */
export interface WindowClassReading {
  readonly known: WindowClass | null;
  readonly raw: string;
  /** The line's depth in percent (LINE_DEPTH, ARTICLE_LINE_DEPTH), `null` otherwise. */
  readonly depthPct: number | null;
}

/** Bata's weekly label as the pages say it. */
export interface WeekLabel {
  readonly start: string;
  readonly end: string;
  readonly text: string;
  readonly labelMechanic: LabelMechanic | null;
  readonly labelMechanicRaw: string;
  readonly labelDepthPct: number | null;
  readonly mechanic: MechanicReading;
}

/** A commercial week a window overlaps, beside it — never over it. `label` `null`: none landed. */
export interface CommercialWeek {
  readonly year: number;
  readonly week: number;
  readonly sharedDays: number;
  readonly label: WeekLabel | null;
}

/** One channel's impact against the neighbouring days in the same doors. */
export interface ChannelImpact {
  readonly channel: string;
  readonly comparable: boolean;
  readonly touchesPeakMonth: boolean;
  readonly doorsInWindow: number;
  readonly doorsCompared: number;
  /** Exact decimal text in `currency`, shown as written. */
  readonly windowNet: string;
  readonly netUplift: number | null;
  readonly unitsUplift: number | null;
  readonly grossMarginUplift: number | null;
  readonly incrementalNet: string | null;
  readonly incrementalDiscount: string | null;
  readonly discountRate: { readonly window: number | null; readonly neighbour: number | null };
  readonly marginRate: { readonly window: number | null; readonly neighbour: number | null };
  readonly afterWindowNetChange: number | null;
  readonly basis: string;
  readonly currency: string;
}

export interface PromotionWindow {
  readonly id: string;
  readonly start: string;
  readonly end: string;
  readonly days: number;
  readonly windowClass: WindowClassReading;
  readonly freeItemShare: number;
  readonly topDepthShare: number;
  /** `null`: no week of the window carries a label to compare. */
  readonly labelAgrees: boolean | null;
  readonly basis: string;
  readonly mechanic: MechanicReading;
  /** Every overlapping week, in calendar order. */
  readonly weeks: readonly CommercialWeek[];
  /** The week with the most shared days (ties to the earlier) — the label the window is read against. */
  readonly headline: CommercialWeek | null;
  readonly impacts: readonly ChannelImpact[];
}

export interface PromotionWindows {
  readonly market: string;
  readonly from: string;
  readonly to: string;
  /** In the platform's order: newest first. */
  readonly windows: readonly PromotionWindow[];
}

const isMechanic = (value: string | null): value is PromotionMechanic => (PROMOTION_MECHANICS as readonly string[]).includes(value ?? '');
const isWindowClass = (value: string): value is WindowClass => (WINDOW_CLASSES as readonly string[]).includes(value);
const isLabelMechanic = (value: string): value is LabelMechanic => (LABEL_MECHANICS as readonly string[]).includes(value);

/** The classes whose group key carries a depth suffix — the longer name first, so it is not read as the shorter. */
const DEPTH_GROUP = /^(ARTICLE_LINE_DEPTH|LINE_DEPTH)_(\d+)$/;

/**
 * The impact mart's group key (`windowKey`: `FREE_ITEM`, `LOW`, `LINE_DEPTH_50`,
 * `ARTICLE_LINE_DEPTH_30`) read as a window class. A key of no known shape keeps `known: null`. Pure.
 */
export function windowGroupClass(group: string): WindowClassReading {
  const depth = DEPTH_GROUP.exec(group);
  if (depth !== null) return { known: depth[1] as WindowClass, raw: group, depthPct: Number(depth[2]) };
  // A depth class with no depth is not a shape the mart publishes: no words, rather than «at 0% off».
  return { known: group === 'FREE_ITEM' || group === 'LOW' ? group : null, raw: group, depthPct: null };
}

/** The mechanic as the pages say it. Pure. */
export function mechanicOf(code: string | null, registered: boolean): MechanicReading {
  return { code: isMechanic(code) ? code : null, rawCode: code, registered: registered && isMechanic(code) };
}

const weekOrder = (a: { readonly year: number; readonly week: number }, b: { readonly year: number; readonly week: number }): number => (
  (a.year - b.year) || (a.week - b.week)
);

/** The week a window is read against: the most shared days, ties to the earlier (bata-data ADR 8006 §8). Pure. */
export function headlineWeek(weeks: readonly CommercialWeek[]): CommercialWeek | null {
  let best: CommercialWeek | null = null;
  for (const week of weeks) {
    if (best === null || week.sharedDays > best.sharedDays || (week.sharedDays === best.sharedDays && weekOrder(week, best) < 0)) best = week;
  }
  return best;
}

/** A calendar week's label as the pages read it. Pure. */
export function weekLabelOf(week: CommercialWeekShape): WeekLabel {
  return {
    start: week.weekStartDate,
    end: week.weekEndDate,
    text: week.promotionText,
    labelMechanic: isLabelMechanic(week.labelMechanic) ? week.labelMechanic : null,
    labelMechanicRaw: week.labelMechanic,
    labelDepthPct: week.labelDepthPct,
    mechanic: mechanicOf(week.mechanicCode, week.mechanicRegistered),
  };
}

/** The windows read as the pages read it. Pure. */
export function promotionWindowsOf(page: PromotionWindowsShape): PromotionWindows {
  return {
    market: page.countryKey,
    from: page.from,
    to: page.to,
    windows: page.windows.map((window) => {
      const weeks = window.overlappingWeeks
        .map((week): CommercialWeek => ({
          year: week.calendarYear,
          week: week.commercialWeek,
          sharedDays: week.sharedDays,
          label: week.label === null ? null : weekLabelOf(week.label),
        }))
        .sort(weekOrder);
      return {
        id: window.windowId,
        start: window.windowStartDate,
        end: window.windowEndDate,
        days: window.windowDays,
        windowClass: {
          known: isWindowClass(window.windowClass) ? window.windowClass : null,
          raw: window.windowClass,
          depthPct: window.lineDepthPct,
        },
        freeItemShare: window.freeItemShareMean,
        topDepthShare: window.topDepthShareMean,
        labelAgrees: window.labelAgreesWithTills,
        basis: window.windowBasis,
        mechanic: mechanicOf(window.mechanicCode, window.mechanicRegistered),
        weeks,
        headline: headlineWeek(weeks),
        impacts: window.impacts.map((impact): ChannelImpact => ({
          channel: impact.channelKey,
          comparable: impact.isComparable,
          touchesPeakMonth: impact.touchesPeakMonth,
          doorsInWindow: impact.doorsInWindow,
          doorsCompared: impact.doorsCompared,
          windowNet: impact.windowNetValueAmount,
          netUplift: impact.netUplift,
          unitsUplift: impact.unitsUplift,
          grossMarginUplift: impact.grossMarginUplift,
          incrementalNet: impact.incrementalNetValueAmount,
          incrementalDiscount: impact.incrementalDiscountValueAmount,
          discountRate: { window: impact.windowDiscountRate, neighbour: impact.neighbourDiscountRate },
          marginRate: { window: impact.windowGrossMarginRate, neighbour: impact.neighbourGrossMarginRate },
          afterWindowNetChange: impact.postWindowNetChange,
          basis: impact.identification,
          currency: impact.currency,
        })),
      };
    }),
  };
}

/** One mechanic group × channel as the panel reads it. */
export interface MechanicImpact {
  /** The mart's group key (a class and depth, e.g. a 50% line) — what makes the row one row. */
  readonly group: string;
  readonly channel: string;
  readonly mechanic: MechanicReading;
  readonly windows: number;
  /** Always an estimate; `low`/`high` `null` below two windows — one comparison states no spread. */
  readonly netUplift: { readonly estimate: number; readonly low: number | null; readonly high: number | null };
  readonly discountRateChange: number | null;
  readonly marginRateChange: number | null;
  /** The comparison the row rests on — the neighbouring days in the same doors. */
  readonly basis: string;
}

export interface PromotionMechanics {
  readonly market: string;
  readonly rows: readonly MechanicImpact[];
}

/** The mechanics read as the panel reads it, in the platform's order. Pure. */
export function promotionMechanicsOf(page: PromotionMechanicsShape): PromotionMechanics {
  return {
    market: page.countryKey,
    rows: page.mechanics.map((row) => ({
      group: row.windowKey,
      channel: row.channelKey,
      mechanic: mechanicOf(row.mechanicCode, row.mechanicRegistered),
      windows: row.windowCount,
      // Both bounds or neither: a half interval is not shown as an interval.
      netUplift: row.netUplift.low === null || row.netUplift.high === null
        ? { estimate: row.netUplift.estimate, low: null, high: null }
        : { estimate: row.netUplift.estimate, low: row.netUplift.low, high: row.netUplift.high },
      discountRateChange: row.discountRateChange,
      marginRateChange: row.grossMarginRateChange,
      basis: row.basis,
    })),
  };
}

// ── reads 4 and 5: a window's reference perimeter (bata-platform ADR 8016) ─────
//
// Owner demo 2026-10-09 request 6, bata-ui issue 314. Pinned 2026-10-09 to bata-platform main
// `7fbe1f9` (PR 641):
//
//   GET /api/v1/postseason/promotions/windows/{window_id}/doors?country_key=
//   GET /api/v1/postseason/promotions/windows/{window_id}/articles?country_key=
//
// The doors come from bata-data's fifth register (its ADR 8012): every door that traded on a window
// day OR a neighbouring day, whether it entered the comparison and, when not, why. The articles are
// summed from the landed article-day panel over the window's days. Both in key order, never
// ranked. Nothing landed answers 404 PROMOTIONS_UNAVAILABLE; an unknown window 404
// PROMOTION_WINDOW_NOT_FOUND.

/** Why a door that traded around a window did not enter its comparison. */
export const NOT_COMPARED_REASONS = ['no_neighbour_trading_day', 'no_window_trading_day'] as const;
export type NotComparedReason = (typeof NOT_COMPARED_REASONS)[number];

export type WindowDoorsShape = WindowDoorsModel;
export type WindowArticlesShape = WindowArticlesModel;

export interface PromotionWindowPerimeterParams {
  readonly path: { readonly window_id: string };
  readonly query: { readonly country_key: string };
}

export type PromotionWindowDoorsRead = OperationSpec<GeneratedPromotionWindowDoorsParams, WindowDoorsShape>;
export type PromotionWindowArticlesRead = OperationSpec<GeneratedPromotionWindowArticlesParams, WindowArticlesShape>;

export const PROMOTION_WINDOW_DOORS_PATH_IN_SPEC = '/api/v1/postseason/promotions/windows/{window_id}/doors';
export const PROMOTION_WINDOW_ARTICLES_PATH_IN_SPEC = '/api/v1/postseason/promotions/windows/{window_id}/articles';

/** The generated operations (pinned 2026-10-09); `null` would draw «In arrivo» and read nothing. */
export const PROMOTION_WINDOW_DOORS_READ: PromotionWindowDoorsRead | null = promotionWindowDoors;
export const PROMOTION_WINDOW_ARTICLES_READ: PromotionWindowArticlesRead | null = promotionWindowArticles;

/** One window of one market — both required; nothing is chosen for the planner (G16). */
export function promotionWindowPerimeterParams(market: string, windowId: string): PromotionWindowPerimeterParams {
  return { path: { window_id: windowId }, query: { country_key: market } };
}

/** One door of one channel around the window. */
export interface WindowDoor {
  readonly channel: string;
  readonly store: string;
  /** `null`: the door master in force names no such store — never filled with the key. */
  readonly storeName: string | null;
  readonly windowDays: number;
  readonly neighbourDays: number;
  readonly compared: boolean;
  /** `null` exactly when compared; a reason this build has no words for is kept verbatim. */
  readonly reason: NotComparedReason | null;
  readonly reasonRaw: string | null;
  readonly units: number;
  /** Exact decimal text in `currency`, shown as written. */
  readonly net: string;
  readonly currency: string;
}

/** One channel's door counts, counted by the platform from the listed doors. */
export interface DoorChannel {
  readonly channel: string;
  readonly inWindow: number;
  readonly compared: number;
  readonly neighbourOnly: number;
}

export interface WindowDoors {
  readonly market: string;
  readonly windowId: string;
  readonly start: string;
  readonly end: string;
  readonly channels: readonly DoorChannel[];
  /** In the platform's order: channel, then door. */
  readonly doors: readonly WindowDoor[];
  readonly basis: string;
}

/** One article of one channel over the window's days. */
export interface WindowArticle {
  readonly channel: string;
  readonly article: string;
  /** `null`: no attribute row in force names it (none does on production today). */
  readonly articleName: string | null;
  readonly majorCategory: string | null;
  readonly category: string | null;
  readonly subCategory: string | null;
  readonly brand: string | null;
  readonly units: number;
  /** Units on the tills' promotion lines (bata-data ADR 8010). */
  readonly promotionalUnits: number;
  /** The article's share of the channel's window units; `null` when those total nought. */
  readonly unitsShare: number | null;
  /** Booked net, exact decimal text in `currency`. */
  readonly net: string;
  readonly currency: string;
}

export interface ArticleChannel {
  readonly channel: string;
  readonly articles: number;
  readonly units: number;
  readonly promotionalUnits: number;
}

export interface WindowArticles {
  readonly market: string;
  readonly windowId: string;
  readonly start: string;
  readonly end: string;
  /** Distinct articles across the channels returned. */
  readonly articleCount: number;
  readonly channels: readonly ArticleChannel[];
  /** In the platform's order: channel, then article. */
  readonly articles: readonly WindowArticle[];
  readonly netBasis: string;
}

const isNotComparedReason = (value: string | null): value is NotComparedReason => (NOT_COMPARED_REASONS as readonly string[]).includes(value ?? '');

/** The doors read as the pages read it. Pure. */
export function windowDoorsOf(page: WindowDoorsShape): WindowDoors {
  return {
    market: page.countryKey,
    windowId: page.window.windowId,
    start: page.window.windowStartDate,
    end: page.window.windowEndDate,
    channels: page.channels.map((channel) => ({
      channel: channel.channelKey,
      inWindow: channel.doorsInWindow,
      compared: channel.doorsCompared,
      neighbourOnly: channel.doorsNeighbourOnly,
    })),
    doors: page.doors.map((door) => ({
      channel: door.channelKey,
      store: door.storeKey,
      storeName: door.storeName,
      windowDays: door.windowTradingDays,
      neighbourDays: door.neighbourTradingDays,
      compared: door.isCompared,
      reason: isNotComparedReason(door.notComparedReason) ? door.notComparedReason : null,
      reasonRaw: door.notComparedReason,
      units: door.windowUnits,
      net: door.windowNetValueAmount,
      currency: door.currency,
    })),
    basis: page.basis,
  };
}

/** The articles read as the pages read it. Pure. */
export function windowArticlesOf(page: WindowArticlesShape): WindowArticles {
  return {
    market: page.countryKey,
    windowId: page.window.windowId,
    start: page.window.windowStartDate,
    end: page.window.windowEndDate,
    articleCount: page.articleCount,
    channels: page.channels.map((channel) => ({
      channel: channel.channelKey,
      articles: channel.articles,
      units: channel.units,
      promotionalUnits: channel.promotionalUnits,
    })),
    articles: page.articles.map((article) => ({
      channel: article.channelKey,
      article: article.articleKey,
      articleName: article.articleName,
      majorCategory: article.majorCategory,
      category: article.category,
      subCategory: article.subCategory,
      brand: article.brandCode,
      units: article.units,
      promotionalUnits: article.promotionalUnits,
      unitsShare: article.unitsShare,
      net: article.bookedNetValueAmount,
      currency: article.currency,
    })),
    netBasis: page.netBasis,
  };
}

// ── read 6: one store's sub-categories in a window (bata-platform ADR 8017) ────
//
// bata-ui issue 314's second level. Pinned 2026-10-10 to bata-platform main (ADR 8017):
//
//   GET /api/v1/postseason/promotions/windows/{window_id}/doors/{store_key}/subcategories?country_key=
//
// From bata-data's sixth register (its ADR 8013), one batch per window: per channel, the store as
// the door register states it beside the sum of its sub-categories (`reconcilesToDoor`), then each
// sub-category — booked units and net in the window and on the neighbouring days, the share of the
// store's window units, and the uplift against the neighbouring days in the same store, weekday-
// adjusted, or why none is stated. Key order, never ranked. Nothing landed answers 404
// PROMOTIONS_UNAVAILABLE; an unknown window PROMOTION_WINDOW_NOT_FOUND; a store the door register
// does not hold around the window PROMOTION_WINDOW_DOOR_NOT_FOUND.

/** Why a sub-category was not compared: the store's two reasons, or none sold around the window. */
export const SUBCATEGORY_NOT_COMPARED_REASONS = ['no_neighbour_trading_day', 'no_window_trading_day', 'no_neighbour_units'] as const;
export type SubcategoryNotComparedReason = (typeof SUBCATEGORY_NOT_COMPARED_REASONS)[number];

/** Why a COMPARED sub-category states no uplift (bata-data ADR 8013). */
export const UPLIFT_ABSENT_REASONS = ['adjusted_neighbour_nonpositive'] as const;
export type UpliftAbsentReason = (typeof UPLIFT_ABSENT_REASONS)[number];

export type DoorSubcategoriesShape = DoorSubcategoriesModel;

export interface PromotionWindowStoreParams {
  readonly path: { readonly window_id: string; readonly store_key: string };
  readonly query: { readonly country_key: string };
}

export type PromotionWindowDoorSubcategoriesRead = OperationSpec<GeneratedPromotionWindowDoorSubcategoriesParams, DoorSubcategoriesShape>;

export const PROMOTION_WINDOW_DOOR_SUBCATEGORIES_PATH_IN_SPEC = '/api/v1/postseason/promotions/windows/{window_id}/doors/{store_key}/subcategories';

/** The generated operation (pinned 2026-10-10); `null` would draw «In arrivo» and read nothing. */
export const PROMOTION_WINDOW_DOOR_SUBCATEGORIES_READ: PromotionWindowDoorSubcategoriesRead | null = promotionWindowDoorSubcategories;

/** One store of one window of one market — all three required; nothing is chosen for the planner (G16). */
export function promotionWindowStoreParams(market: string, windowId: string, store: string): PromotionWindowStoreParams {
  return { path: { window_id: windowId, store_key: store }, query: { country_key: market } };
}

/** An uplift, or why a compared sub-category states none. Never a nought in place of an absence. */
export interface UpliftReading {
  readonly value: number | null;
  /** `null` when there is a value, or when the sub-category was not compared (its own reason says why). */
  readonly absentReason: UpliftAbsentReason | null;
  /** The platform's text, kept for a reason this build has no words for. */
  readonly absentReasonRaw: string | null;
}

/** One sub-category of the store over the window's days and the neighbouring days. */
export interface StoreSubcategory {
  readonly channel: string;
  /** `null`: the article master states none — never filled in. */
  readonly categoryCode: string | null;
  readonly category: string | null;
  readonly subCategoryCode: string | null;
  readonly subCategory: string | null;
  readonly units: number;
  /** Booked net, exact decimal text in `currency`. */
  readonly net: string;
  readonly neighbourUnits: number;
  readonly neighbourNet: string;
  /** The share of the store's window units in the channel; `null` when those total nought. */
  readonly unitsShare: number | null;
  readonly compared: boolean;
  readonly reason: SubcategoryNotComparedReason | null;
  readonly reasonRaw: string | null;
  readonly unitsUplift: UpliftReading;
  readonly netUplift: UpliftReading;
  readonly currency: string;
}

/** The store in one channel, as the door register states it, beside the sum of its sub-categories. */
export interface StoreChannel {
  readonly channel: string;
  readonly windowDays: number;
  readonly neighbourDays: number;
  readonly compared: boolean;
  readonly reason: NotComparedReason | null;
  readonly reasonRaw: string | null;
  readonly storeUnits: number;
  readonly storeNet: string;
  readonly subcategoryUnits: number;
  readonly subcategoryNet: string;
  readonly subcategories: number;
  /** False: the two registers come from different builds, and the page says so. */
  readonly reconciles: boolean;
  readonly currency: string;
}

export interface StoreSubcategories {
  readonly market: string;
  readonly windowId: string;
  readonly start: string;
  readonly end: string;
  readonly store: string;
  readonly storeName: string | null;
  /** The days on each side the comparison reads; `null` when the batch states none. */
  readonly neighbourDayCount: number | null;
  readonly channels: readonly StoreChannel[];
  /** In the platform's order: channel, then category and sub-category, the unlabelled last. */
  readonly subcategories: readonly StoreSubcategory[];
  readonly basis: string;
  readonly netBasis: string;
}

const isSubcategoryReason = (value: string | null): value is SubcategoryNotComparedReason => (SUBCATEGORY_NOT_COMPARED_REASONS as readonly string[]).includes(value ?? '');
const isUpliftAbsentReason = (value: string | null): value is UpliftAbsentReason => (UPLIFT_ABSENT_REASONS as readonly string[]).includes(value ?? '');

function upliftOf(value: number | null, absentReason: string | null): UpliftReading {
  return { value, absentReason: isUpliftAbsentReason(absentReason) ? absentReason : null, absentReasonRaw: absentReason };
}

/** The store's sub-categories as the page reads them. Pure. */
export function storeSubcategoriesOf(page: DoorSubcategoriesShape): StoreSubcategories {
  return {
    market: page.countryKey,
    windowId: page.window.windowId,
    start: page.window.windowStartDate,
    end: page.window.windowEndDate,
    store: page.storeKey,
    storeName: page.storeName,
    neighbourDayCount: page.neighbourDays,
    channels: page.channels.map((channel) => ({
      channel: channel.channelKey,
      windowDays: channel.windowTradingDays,
      neighbourDays: channel.neighbourTradingDays,
      compared: channel.isCompared,
      reason: isNotComparedReason(channel.notComparedReason) ? channel.notComparedReason : null,
      reasonRaw: channel.notComparedReason,
      storeUnits: channel.doorWindowUnits,
      storeNet: channel.doorWindowNetValueAmount,
      subcategoryUnits: channel.subCategoryWindowUnits,
      subcategoryNet: channel.subCategoryWindowNetValueAmount,
      subcategories: channel.subCategories,
      reconciles: channel.reconcilesToDoor,
      currency: channel.currency,
    })),
    subcategories: page.subCategories.map((line) => ({
      channel: line.channelKey,
      categoryCode: line.categoryCode,
      category: line.category,
      subCategoryCode: line.subCategoryCode,
      subCategory: line.subCategory,
      units: line.windowUnits,
      net: line.windowNetValueAmount,
      neighbourUnits: line.neighbourUnits,
      neighbourNet: line.neighbourNetValueAmount,
      unitsShare: line.unitsShare,
      compared: line.isCompared,
      reason: isSubcategoryReason(line.notComparedReason) ? line.notComparedReason : null,
      reasonRaw: line.notComparedReason,
      unitsUplift: upliftOf(line.unitsUplift, line.unitsUpliftAbsentReason),
      netUplift: upliftOf(line.netUplift, line.netUpliftAbsentReason),
      currency: line.currency,
    })),
    basis: page.basis,
    netBasis: page.netBasis,
  };
}
